/**
 * O que faz: grava microfone com limite de duração/tamanho e libera recursos ao sair da tela.
 * Onde usa: GravadorConhecimentoClinica na aba Base de conhecimento.
 * Conexões: MediaRecorder, getUserMedia e Web Audio (amplitude do círculo, sem render React por quadro).
 * Tabelas: Nenhuma; áudio só é enviado depois de finalizar a gravação.
 * Data: 29/09/2026.
 */
import { useEffect, useRef, useState } from 'react';
import { LIMITE_AUDIO_BYTES } from '../../../supabase/functions/_shared/clinic-knowledge';

const LIMITE_SEGUNDOS = 300;

export function useGravacaoConhecimento(aoConcluir: (arquivo: File) => void) {
  const [gravando, setGravando] = useState(false);
  const [preparando, setPreparando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [erro, setErro] = useState<string | null>(null);
  const circuloRef = useRef<HTMLDivElement>(null);
  const gravador = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const contextoAudio = useRef<AudioContext | null>(null);
  const quadro = useRef<number>();
  const intervalo = useRef<ReturnType<typeof setInterval>>();
  const montado = useRef(true);
  const cancelado = useRef(false);
  const iniciando = useRef(false);
  const callback = useRef(aoConcluir);
  callback.current = aoConcluir;

  const liberarMicrofone = () => {
    if (quadro.current !== undefined) cancelAnimationFrame(quadro.current);
    clearInterval(intervalo.current);
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    void contextoAudio.current?.close().catch(() => undefined);
    contextoAudio.current = null;
  };

  useEffect(() => {
    montado.current = true;
    return () => {
      // Trocar clínica/aba ou fechar editor nunca pode deixar o microfone ligado nem importar na clínica nova.
      montado.current = false;
      cancelado.current = true;
      if (gravador.current?.state === 'recording') gravador.current.stop();
      liberarMicrofone();
    };
  }, []);

  const parar = (descartar = false) => {
    cancelado.current = descartar;
    if (gravador.current?.state === 'recording') gravador.current.stop();
    liberarMicrofone();
    setGravando(false);
  };

  const iniciar = async () => {
    // Reentrada e falta de HTTPS/microfone são recusadas antes de pedir permissão ao navegador.
    if (iniciando.current || gravador.current?.state === 'recording') return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setErro('Este navegador não permite gravação. Use Chrome, Edge ou Safari atualizado com acesso seguro.');
      return;
    }
    iniciando.current = true;
    setPreparando(true);
    setErro(null);
    cancelado.current = false;
    let captura: MediaStream | null = null;
    try {
      captura = await navigator.mediaDevices.getUserMedia({ audio: true });
      // A permissão pode chegar após sair da página; nesse caso libera o microfone imediatamente.
      if (!montado.current) { captura.getTracks().forEach((track) => track.stop()); return; }
      stream.current = captura;
      const mimeType = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm']
        .find((tipo) => MediaRecorder.isTypeSupported(tipo));
      const recorder = new MediaRecorder(captura, mimeType ? { mimeType } : undefined);
      gravador.current = recorder;
      const partes: Blob[] = [];
      let bytes = 0;
      let excedeuLimite = false;
      recorder.ondataavailable = (evento) => {
        bytes += evento.data.size;
        // Nunca envia áudio parcial com tamanho excedido: a pessoa precisa gravar uma versão menor.
        if (bytes > LIMITE_AUDIO_BYTES) {
          excedeuLimite = true;
          if (recorder.state === 'recording') recorder.stop();
          liberarMicrofone();
        } else partes.push(evento.data);
      };
      recorder.onerror = () => {
        cancelado.current = true;
        liberarMicrofone();
        if (montado.current) { setErro('A gravação foi interrompida. Verifique o microfone e tente novamente.'); setGravando(false); }
      };
      recorder.onstop = () => {
        liberarMicrofone();
        if (!montado.current) return;
        setGravando(false);
        if (excedeuLimite) { setErro('O áudio excedeu 20 MB. Grave uma versão menor.'); return; }
        if (cancelado.current) return;
        const tipo = recorder.mimeType || partes[0]?.type || 'audio/webm';
        const blob = new Blob(partes, { type: tipo.split(';')[0] });
        if (!blob.size) { setErro('A gravação ficou vazia. Grave novamente.'); return; }
        const extensao = tipo.includes('mp4') ? 'm4a' : tipo.includes('ogg') ? 'ogg' : 'webm';
        callback.current(new File([blob], `conhecimento.${extensao}`, { type: blob.type }));
      };
      // O analisador é só feedback visual: falhar no Web Audio não impede capturar áudio válido.
      try {
        const contexto = new AudioContext();
        contextoAudio.current = contexto;
        const analisador = contexto.createAnalyser();
        analisador.fftSize = 256;
        contexto.createMediaStreamSource(captura).connect(analisador);
        const amostras = new Uint8Array(analisador.fftSize);
        const atualizarAmplitude = () => {
          analisador.getByteTimeDomainData(amostras);
          const energia = Math.sqrt(amostras.reduce((soma, valor) => soma + ((valor - 128) / 128) ** 2, 0) / amostras.length);
          circuloRef.current?.style.setProperty('--volume-voz', String(Math.min(1, energia * 6)));
          quadro.current = requestAnimationFrame(atualizarAmplitude);
        };
        atualizarAmplitude();
      } catch { /* A gravação segue com círculo estático quando o analisador não está disponível. */ }
      recorder.start(1000);
      setSegundos(0);
      setGravando(true);
      let tempo = 0;
      intervalo.current = setInterval(() => {
        tempo++;
        if (montado.current) setSegundos(tempo);
        if (tempo >= LIMITE_SEGUNDOS) { if (recorder.state === 'recording') recorder.stop(); liberarMicrofone(); }
      }, 1000);
    } catch {
      captura?.getTracks().forEach((track) => track.stop());
      liberarMicrofone();
      if (montado.current) setErro('Não foi possível acessar o microfone. Permita o acesso no navegador e tente novamente.');
    } finally {
      iniciando.current = false;
      if (montado.current) setPreparando(false);
    }
  };

  return { gravando, preparando, segundos, erro, circuloRef, iniciar, parar };
}
